import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";

import { dispatchEvent } from "./dispatch-event.js";

@Component({
    tag: "ux-static-filter",
    styleUrl: "static-filter.scss",
    shadow: false
})
export class StaticFilter {
    private baseClass = "ux-static-filter";
    /** do not change the value of this attribute, as other components have a dependency to it */
    private hiddenItemClass = "ux-static-filter__item--hidden";
    private filteredItemClass = "ux-static-filter__item--filtered";
    private itemsFilterAttribute = "data-ux-static-filter";

    @Element() element: HTMLUxStaticFilterElement;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    @State() selectedValues: any = {};
    @State() handleAnimation = false;
    @State() filterOnLoad = true;

    /** the maximum number of items to show at any given time */
    @Prop() readonly maxItemsToShow: number | undefined = undefined;
    /** comma separated list of string values, by which all elements should be displayed */
    @Prop() readonly displayAllValues: string = undefined;
    /** if present on the target ux-select, ux-radio-list-item or ux-radio-button
     *  it will replace the "name" attribute for setting the filter name */
    @Prop() readonly nameAttributeReplacer: string = "data-ux-static-filter-name";
    /** adds the --filtered instead of the --hidden class to 'not active' filtered elements */
    @Prop() readonly hasFilteredClass: boolean = false;

    /**
     * uxStaticFilter Event
     */
    @Event({
        eventName: "uxStaticFilter",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    staticFilterEvent: EventEmitter<{
        hidden: boolean;
        htmlElement: HTMLElement;
    }>;

    private onChangeEvent = (e: CustomEvent) => {
        const eTarget = e.target as HTMLUxRadioListItemElement | HTMLUxSelectElement;
        const eTargetValue = eTarget.querySelector("input, select") as HTMLInputElement;
        if (this.filterOnLoad) {
            this.filterOnLoad = false;
        }
        this.selectedValues = {
            ...this.selectedValues,
            [this.getElementNameValue(eTarget) || eTarget.name]: eTargetValue.value
        };
    };

    private getElementNameValue = (element: HTMLElement) => {
        return (
            element.closest("ux-select")?.getAttribute(this.nameAttributeReplacer) ||
            element.closest("ux-radio-button")?.getAttribute(this.nameAttributeReplacer) ||
            element.closest("ux-radio-list-item")?.getAttribute(this.nameAttributeReplacer) ||
            null
        );
    };

    componentDidLoad() {
        const radioButtonElements = Array.from(
            this.element.querySelectorAll("ux-radio-button input:checked")
        ) as HTMLInputElement[];
        const selectElements = Array.from(this.element.querySelectorAll("ux-select select")) as HTMLSelectElement[];
        const radioButtonsValues = radioButtonElements.reduce((acc, element) => {
            return {
                ...acc,
                [this.getElementNameValue(element) || element.name]: element.value
            };
        }, {});
        const selectValues = selectElements.reduce((acc, element) => {
            return {
                ...acc,
                [this.getElementNameValue(element) || element.name]: element.value
            };
        }, {});
        this.selectedValues = {
            ...radioButtonsValues,
            ...selectValues
        };
    }

    private filterItems = async () => {
        const itemsToFilter = this.element.querySelectorAll(`[${this.itemsFilterAttribute}]`);
        /** 0.2s timer before we show or display items */
        if (!this.filterOnLoad) {
            await new Promise((resolve) => setTimeout(resolve, 200));
        }
        let counter = 0;
        itemsToFilter.forEach((item: HTMLElement) => {
            let showItem = false;
            const itemFilters = item
                .getAttribute(this.itemsFilterAttribute)
                .split(",")
                .map((value) => value.trim());
            const filters =
                typeof this.displayAllValues === "string"
                    ? [...itemFilters, ...this.displayAllValues.split(",").map((value) => value.trim())]
                    : [...itemFilters];
            filters.forEach((filter: string) => {
                const filterName = filter.split("=")[0];
                const filterValue = filter.split("=")[1];
                if (this.selectedValues[filterName] === filterValue) {
                    showItem = true;
                }
            });
            if (showItem && (counter < this.maxItemsToShow || this.maxItemsToShow === undefined)) {
                this.showItem(item);
                counter++;
            } else {
                this.hideItem(item);
            }
        });
    };

    private showItem = (item: HTMLElement): void => {
        if (!this.hasFilteredClass && item.classList.contains(this.hiddenItemClass)) {
            item.classList.remove(this.hiddenItemClass);
        } else if (this.hasFilteredClass && item.classList.contains(this.filteredItemClass)) {
            item.classList.remove(this.filteredItemClass);
        }
        this.staticFilterEvent.emit({ hidden: false, htmlElement: item });
    };

    private hideItem = (item: HTMLElement): void => {
        if (!this.hasFilteredClass && !item.classList.contains(this.hiddenItemClass)) {
            item.classList.add(this.hiddenItemClass);
        } else if (this.hasFilteredClass && !item.classList.contains(this.filteredItemClass)) {
            item.classList.add(this.filteredItemClass);
        }
        this.staticFilterEvent.emit({ hidden: true, htmlElement: item });
    };

    @Watch("selectedValues")
    onSelectedValueChange() {
        this.handleAnimation = true;
    }

    async componentDidRender(): Promise<void> {
        if (this.handleAnimation) {
            await this.filterItems();
            this.handleAnimation = false;
            dispatchEvent("resize");
        }
    }

    render() {
        return (
            <Host
                onUxRadioListItemChange={this.onChangeEvent}
                onUxSelectChange={this.onChangeEvent}
                onUxRadioButtonChange={this.onChangeEvent}
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--hidden-items`]: this.handleAnimation
                }}
            >
                <slot />
            </Host>
        );
    }
}
